import { AutocompleteSuggestion, PlacesDetails } from './GoogleMapsApiHandler';

export const mapToDeliveryAddress = (
  placeId: string,
  data: AutocompleteSuggestion | PlacesDetails,
): DeliveryAddress => {
  if ('placePrediction' in data) {
    return {
      displayAddress: {
        firstLine: data.placePrediction.structuredFormat.mainText.text,
        secondLine: data.placePrediction.structuredFormat.secondaryText.text,
      },
      formattedAddress: data.placePrediction.text.text,
      placeId,
    };
  } else {
    return {
      addressComponents: {
        unit: getAddressComponentFromPlaceDetails(data, 'subpremise'),
        streetNumber: getAddressComponentFromPlaceDetails(
          data,
          'street_number',
        ),
        streetName: getAddressComponentFromPlaceDetails(data, 'route'),
        suburb:
          getAddressComponentFromPlaceDetails(data, 'locality') ||
          getAddressComponentFromPlaceDetails(data, 'postal_town'),
        state: getAddressComponentFromPlaceDetails(
          data,
          'administrative_area_level_1',
        ),
        postCode: getAddressComponentFromPlaceDetails(data, 'postal_code'),
        country: getAddressComponentFromPlaceDetails(data, 'country'),
      },
      formattedAddress: data.formattedAddress,
      location: data.location,
      displayAddress: getDisplayAddressFromPlaceDetails(data),
      placeId,
    };
  }
};

const getAddressComponentFromPlaceDetails = (
  placeDetail: PlacesDetails,
  type: string,
): string => {
  const addressComponent = placeDetail.addressComponents.find((component) =>
    component.types.includes(type),
  );
  return addressComponent?.longText ?? (addressComponent?.shortText || '');
};

const getDisplayAddressFromPlaceDetails = (
  placeDetail: PlacesDetails,
): DisplayAddress => {
  const addressLines =
    placeDetail.postalAddress?.addressLines?.filter(Boolean) || [];
  const firstLine = addressLines.join(' ').trim();

  let secondLine = [
    placeDetail?.postalAddress?.locality,
    placeDetail?.postalAddress?.administrativeArea,
  ]
    .filter(Boolean)
    .join(', ');

  if (!firstLine && !secondLine) {
    secondLine = placeDetail.formattedAddress;
  }

  return { firstLine, secondLine };
};

export interface FilterAddressSuggestionsParams {
  filter?: string;
  suggestions: DeliveryAddress[];
  savedAddresses: DeliveryAddress[];
  recentAddresses: DeliveryAddress[];
  editingAddress: boolean;
}

export const filterAddressSuggestions = (
  params: FilterAddressSuggestionsParams,
): DeliveryAddress[] => {
  const {
    filter,
    suggestions,
    savedAddresses,
    recentAddresses,
    editingAddress,
  } = params;

  let addresses: DeliveryAddress[] = [];

  if (!savedAddresses || editingAddress) {
    addresses = suggestions;
    return addresses;
  }

  if (!editingAddress) {
    addresses = [...savedAddresses, ...recentAddresses];
  }

  if (filter && savedAddresses) {
    const placeIds = suggestions.map((s) => s.placeId);
    addresses = [
      ...savedAddresses.filter((address) => placeIds.includes(address.placeId)),
      ...suggestions.filter(
        (savedAddress) =>
          !savedAddresses.some(
            (address) => address.placeId === savedAddress.placeId,
          ),
      ),
    ];
  }

  return addresses;
};
